// ============================================================
// constants/sohProfiles.js
// EV Powertrain Simulator — SOH 프로파일 / 제약조건 정의
//
// ★ SSOT(단일 출처) — 이 파일이 SOH_PROFILES의 유일한 정의입니다.
//   - core/physics/battery.js → 이 파일에서 import (globalThis 런타임 오버라이드 유지)
//   - ev-sim_v2_0.jsx → 이 파일에서 import (기존 그대로)
//   - 수치 변경 시 이 파일 1곳만 수정.
//
// [Fix-SSOT-Bcyc] 구버전 B_cyc 값(절대 Arrhenius 기반)을
//   battery.js KPI-Fix-Bcyc 교정값(상대 Arrhenius + 멱함수)으로 통일.
//   nmc_622: 7.54e-3 → 1.30e-3
//   nmc_811: 9.2e-3  → 1.73e-3
//   lfp    : 3.5e-3  → 5.40e-4
//   nca    : 11.0e-3 → 1.83e-3
// ============================================================

/**
 * SOH_PROFILES — 셀 화학계별 SOH 열화 모델 파라미터
 * 출처: Arrhenius 캘린더 열화(Ea_cal) + 멱함수 사이클 열화(B_cyc, z_DOD) 결합 모델.
 *       수치는 공개 문헌(Schimpe 2017, Wang 2014 등) 기반 교정값.
 * 단위: A_cal [1/√s], Ea_cal/Ea_cyc [J/mol], B_cyc [무차원], z_DOD [무차원],
 *       k_sei_A [1/s], k_sei_Ea [J/mol], cap_fade_eol [무차원]
 * 사용처: battery.js calcSOH(), BatteryHealthTabContent.jsx SOH 열화 곡선 표시
 * 주의: 이 파일이 유일한 SSOT. battery.js는 globalThis 런타임 오버라이드도 지원하나
 *       기본값은 반드시 이 파일에서만 수정.
 *
 * 파라미터 의미:
 *   A_cal   — 캘린더 열화 사전지수 인자 (온도·시간 의존)
 *   Ea_cal  — 캘린더 열화 활성화 에너지 [J/mol]
 *   n_arr   — Arrhenius 시간 멱지수 (0.5 = SEI 확산 지배)
 *   B_cyc   — 사이클 열화 계수 (DOD·사이클 수 의존)
 *   Ea_cyc  — 사이클 열화 활성화 에너지 [J/mol]
 *   z_DOD   — DOD 멱지수 (배터리 화학계별 DOD 민감도)
 *   k_sei_A — SEI 성장 사전지수 인자
 *   k_sei_Ea— SEI 성장 활성화 에너지 [J/mol]
 *   cap_fade_eol — EOL 기준 용량 손실률 (0.20 = 80% 잔존 시 EOL)
 */
const SOH_PROFILES = {
  nmc_622: {
    label: 'NMC 622 (일반)',
    A_cal: 5.0e-4, Ea_cal: 24500, n_arr: 0.5,
    // [Fix-SSOT-Bcyc] 구: 7.54e-3 → 신: 1.30e-3 (battery.js KPI-Fix-Bcyc와 동기화)
    B_cyc: 1.30e-3, Ea_cyc: 31700, z_DOD: 0.55,
    k_sei_A: 2.5e-7, k_sei_Ea: 30000,
    cap_fade_eol: 0.20,   // 80% 잔존 @ EOL
  },
  nmc_811: {
    label: 'NMC 811 (고에너지)',
    A_cal: 8.0e-4, Ea_cal: 26000, n_arr: 0.5,
    // [Fix-SSOT-Bcyc] 구: 9.2e-3 → 신: 1.73e-3 (battery.js와 동기화)
    B_cyc: 1.73e-3, Ea_cyc: 33000, z_DOD: 0.50,
    k_sei_A: 4.0e-7, k_sei_Ea: 28000,
    cap_fade_eol: 0.20,
  },
  lfp: {
    label: 'LFP (장수명)',
    A_cal: 1.5e-4, Ea_cal: 20000, n_arr: 0.6,
    // [Fix-SSOT-Bcyc] 구: 3.5e-3 → 신: 5.40e-4 (battery.js와 동기화)
    B_cyc: 5.40e-4, Ea_cyc: 27000, z_DOD: 0.65,
    k_sei_A: 0.8e-7, k_sei_Ea: 32000,
    cap_fade_eol: 0.20,
  },
  nca: {
    label: 'NCA (고출력)',
    A_cal: 9.5e-4, Ea_cal: 25500, n_arr: 0.5,
    // [Fix-SSOT-Bcyc] 구: 11.0e-3 → 신: 1.83e-3 (battery.js와 동기화)
    B_cyc: 1.83e-3, Ea_cyc: 32000, z_DOD: 0.48,
    k_sei_A: 5.0e-7, k_sei_Ea: 27500,
    cap_fade_eol: 0.20,
  },
};


/**
 * CONSTRAINT_DEFS — DOE·최적화 모듈용 설계 제약조건 정의
 * 출처: 내부 설계 요구사항 (시뮬레이터 v2 기준)
 * 단위: 각 항목별 unit 필드 참조
 * 사용처: DOETabContent.jsx 제약 위반 판정, SensitivityTabContent.jsx 경계조건
 * 구조: fn(params, results) → 값 > 0 이면 제약 위반 (g(x) ≤ 0 표준 형식)
 */
const CONSTRAINT_DEFS = [
  { id:'C1', name:'주행가능거리 ≥ 목표',    type:'inequality', // g(x) ≤ 0 형식
    fn: (p, res) => (p.range_target || 400) - (res.range_km || 0),
    unit:'km', description:'range_km ≥ range_target' },
  { id:'C2', name:'0→100 km/h ≤ 목표',      type:'inequality',
    fn: (p, res) => (res.time_0_100 || 999) - (p.t100_target || 6.0),
    unit:'s',  description:'time_0_100 ≤ t100_target' },
  { id:'C3', name:'전비 ≤ 목표',             type:'inequality',
    fn: (p, res) => (res.energy_wh_km || 999) - (p.energy_target || 200),
    unit:'Wh/km', description:'energy_wh_km ≤ energy_target' },
  { id:'C4', name:'모터 최고온도 ≤ 제한',    type:'inequality',
    fn: (p, res) => (res.peak_temp_motor || 0) - (p.T_motor_cutoff || 160),
    unit:'°C', description:'peak_temp ≤ T_motor_cutoff' },
  { id:'C5', name:'질량 ≤ 상한',             type:'inequality',
    fn: (p) => p.m - (p.m_max || 2500),
    unit:'kg', description:'m ≤ m_max' },
  { id:'C6', name:'CdA ≥ 하한',              type:'inequality',
    fn: (p) => (p.CdA_min || 0.45) - p.CdA,
    unit:'m²', description:'CdA ≥ CdA_min' },
];


export { SOH_PROFILES, CONSTRAINT_DEFS };